import { Box, Typography, Breadcrumbs, Button, Link, Divider } from '@mui/material'
import { FC, ReactNode } from 'react'
import { useInRouterContext, useLocation } from 'react-router-dom'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { secaoDaRota } from '@/utils/secoes'
import { tokens } from '@/theme'

interface BreadcrumbItem {
  label: string
  href?: string
}

interface PageHeaderProps {
  title: string
  breadcrumbs?: BreadcrumbItem[]
  subtitle?: string
  /**
   * Eyebrow (Figma: caption laranja em caixa alta acima do H1) com a seção do menu
   * ("FINANCEIRO", "ADMINISTRAÇÃO"). Por padrão vem da rota; passe um texto para
   * sobrescrever ou `null` para omitir. Fica fora quando há breadcrumbs — os dois juntos
   * diriam a mesma coisa duas vezes.
   */
  eyebrow?: string | null
  /**
   * Slot de ação à direita (v2, Onda 0). Preferido em código novo; `action` continua
   * aceito para compatibilidade e ambos caem no mesmo lugar.
   */
  acao?: ReactNode
  action?: ReactNode | {
    label: string
    icon?: ReactNode
    onClick: () => void
  }
  children?: ReactNode
}

const eyebrowSx = {
  display: 'block',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: tokens.brandPrimary,
  mb: 0.5,
} as const

const EyebrowTexto: FC<{ texto: string }> = ({ texto }) => (
  <Typography component="span" sx={eyebrowSx}>{texto}</Typography>
)

/** Só existe dentro de um Router — daí ser um componente à parte, e não um hook condicional. */
const EyebrowDaRota: FC = () => {
  const secao = secaoDaRota(useLocation().pathname)
  return secao ? <EyebrowTexto texto={secao} /> : null
}

/**
 * Cabeçalho de página v2 (Figma): eyebrow laranja uppercase + H1 30px + subtítulo +
 * ação à direita. Assinatura compatível com a v1 — nenhum consumidor muda.
 */
export const PageHeader: FC<PageHeaderProps> = ({
  title,
  breadcrumbs = [],
  subtitle,
  eyebrow,
  acao,
  action,
  children,
}) => {
  const dentroDoRouter = useInRouterContext()
  const mostraEyebrow = eyebrow !== null && breadcrumbs.length === 0
  const acaoFinal = acao ?? action
  return (
    <Box sx={{ marginBottom: 3 }}>
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ marginBottom: 2 }}
        >
          {breadcrumbs.map((item, index) =>
            item.href ? (
              <Link
                key={index}
                href={item.href}
                underline="hover"
                color="primary"
                sx={{ fontWeight: 500 }}
              >
                {item.label}
              </Link>
            ) : (
              <Typography key={index} color="textPrimary" sx={{ fontWeight: 500 }}>
                {item.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 2,
        }}
      >
        <Box>
          {mostraEyebrow && (
            eyebrow
              ? <EyebrowTexto texto={eyebrow} />
              : dentroDoRouter ? <EyebrowDaRota /> : null
          )}
          <Typography
            variant="h1"
            sx={{ fontWeight: 700, color: tokens.brandInk, letterSpacing: '-0.03em' }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{ color: tokens.textSecondary, marginTop: 0.5, letterSpacing: '-0.01em' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {acaoFinal && (
          typeof acaoFinal === 'object' && acaoFinal !== null && 'label' in acaoFinal ? (
            <Button
              variant="contained"
              color="primary"
              onClick={acaoFinal.onClick}
              startIcon={acaoFinal.icon}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {acaoFinal.label}
            </Button>
          ) : (
            acaoFinal as ReactNode
          )
        )}
      </Box>

      {children && <Box sx={{ marginBottom: 2 }}>{children}</Box>}

      <Divider />
    </Box>
  )
}

export default PageHeader
